import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Card, Center, Row, Typography } from '@tempmee/react';
import { LabelledSelectField } from '../../../../commonComponents/LabelledSelectField';
import { states } from '../../../../commonComponents/StatePicker';
import { UserProfession } from '../../../../../enums/UserProfession';
import { createAuthorizationCredential } from '../../../../../actions/licensesAction';
import { createLoadingSelector } from '../../../../../apis/selectors';
import { showErrorMessage } from '../../../../../utils';

const professionOptions = [
  { value: UserProfession.RDH, label: 'Dental Hygienist' },
  { value: UserProfession.DA, label: 'Dental Assistant' },
  { value: UserProfession.EFDA, label: 'Expanded Functions DA' },
  { value: UserProfession.FD, label: 'Front Desk' },
  { value: UserProfession.DN, label: 'Associate Dentist' },
];

const stateOptions = states.map((s) => ({ value: s.abbreviation, label: s.abbreviation }));

const SelectRow = ({ label, children }) => (
  <Row justify="between" align="center" p={3} className="border-b border-solid border-divider">
    <Typography variant="body2">{label}</Typography>
    {children}
  </Row>
);

SelectRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export const AuthorizationCredentialAdd = ({ onClose }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.users.currentUser?.id);
  const authorizations = useSelector((state) => state.users.authorizations);

  const loadingSelector = createLoadingSelector(['CREATE_AUTHORIZATION']);
  const isCreating = useSelector((state) => loadingSelector(state));

  const [state, setState] = useState('');
  const [profession, setProfession] = useState('');

  const handleCreate = () => {
    if (!state || !profession) return;

    const hasDuplicate = authorizations?.some(
      (auth) => auth.profession === profession && auth.state === state,
    );

    if (hasDuplicate) {
      showErrorMessage({
        message:
          'This professional already have a license added for this profession type and state. Please edit that license instead.',
      });
      return;
    }

    dispatch(createAuthorizationCredential(userId, { state, profession }));
    onClose();
  };

  return (
    <Center py={3}>
      <Box width="50%">
        <Card outline>
          <SelectRow label="License Status:">
            <Typography variant="body2">-</Typography>
          </SelectRow>

          <SelectRow label="State:">
            <LabelledSelectField
              ariaLabel="State"
              className="w-48"
              options={stateOptions}
              value={state || undefined}
              placeholder="Select State"
              onChange={setState}
            />
          </SelectRow>

          <SelectRow label="Profession:">
            <LabelledSelectField
              ariaLabel="Profession"
              className="w-48"
              options={professionOptions}
              value={profession || undefined}
              placeholder="Select Profession"
              onChange={setProfession}
            />
          </SelectRow>

          <Row justify="center" gap={2} py={6}>
            <Button
              color="primary"
              className="w-36"
              loading={isCreating}
              disabled={!state || !profession || isCreating}
              onClick={handleCreate}
            >
              Add License
            </Button>
            <Button variant="outlined" color="secondary" className="w-36" onClick={onClose}>
              Cancel
            </Button>
          </Row>
        </Card>
      </Box>
    </Center>
  );
};
